import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService } from 'gung-common';
import { first, map, Observable, Subject, takeUntil } from 'rxjs';
import { BackendFeatureService } from '../../services/backend-feature.service';
import { GungGroupBranding, GungGroupBrandingService } from '../../services/group-branding/group-branding.service';
import { S3Image } from '../../models/s3';
import { GungSettingsBaseViewComponent } from '../gung-settings-base-view/gung-settings-base-view.component';

const emptyS3Image = (): S3Image =>
  ({
    category: undefined,
    description: undefined,
    i18n: undefined,
    index: undefined,
    pimDimensionIds: undefined,
    s3ETag: undefined,
    s3Uri: undefined,
    s3lastModifiedDate: undefined,
    id: undefined,
    name: undefined,
    extra: undefined
  }) as unknown as S3Image;

@Component({
  selector: 'lib-customer-branding',
  templateUrl: './customer-branding.component.html',
  standalone: false
})
export class CustomerBrandingComponent extends GungSettingsBaseViewComponent implements OnInit, OnDestroy {
  protected groupBrandingService = inject(GungGroupBrandingService);
  protected featureService = inject(BackendFeatureService);
  protected http = inject(HttpClient);
  protected gungNotificationService = inject(GungNotificationService);
  protected translateService = inject(TranslateService);

  showTab$: Observable<boolean>;
  groups: string[] = [];
  filteredGroups: string[] = [];
  configuredGroupIds = new Set<string>();
  selectedGroup: string;
  branding: GungGroupBranding;
  saving = false;

  private destroyed$ = new Subject<void>();

  objectFileImagePreview: { [s: string]: S3Image } = {};

  private readonly colorKeys = [
    'primary',
    'secondary',
    'hover',
    'contrasting',
    'navbarcolor',
    'navbarfontcolor',
    'navbarfonthovercolor'
  ];

  textFormattingOptionList = [
    { id: '', name: '' },
    { id: 'bold', name: 'BOLD' },
    { id: 'italic', name: 'ITALIC' },
    { id: 'link', name: 'LINK' }
  ];

  bannerHeightOptionList = [
    { id: '40', name: 'SMALL' },
    { id: '60', name: 'MEDIUM' },
    { id: '80', name: 'LARGE' }
  ];

  ngOnInit(): void {
    this.showTab$ = this.featureService.isActivated('customer-branding');
    this.groupBrandingService.getBrandableGroups().subscribe(groups => {
      this.groups = groups;
      this.filteredGroups = [...groups];
    });
    // Reactive: getAll() is backed by a subject that refreshes on save, so the badge set updates
    // live after a save and re-entering the tab costs no backend request.
    this.groupBrandingService
      .getAll()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(all => {
        this.configuredGroupIds = new Set(all.map(b => b.id));
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  filter(term: string): void {
    const t = (term || '').toLowerCase();
    this.filteredGroups = this.groups.filter(g => g.toLowerCase().includes(t));
  }

  selectGroup(group: string): void {
    this.selectedGroup = group;
    // Read the group from the shared in-memory list (no backend call). Clone it so form edits
    // don't mutate the subject's copy that other subscribers (and the badge set) share.
    this.groupBrandingService
      .getAll()
      .pipe(first())
      .subscribe(list => {
        const existing = list.find(b => b.id === group);
        const source = existing ? this.cloneBranding(existing) : { id: group, colors: {}, extra: {} };
        this.branding = this.ensureBrandingDefaults(source);
        this.objectFileImagePreview = {
          logo: this.branding.logo?.s3Uri && this.branding.logo,
          'extra.navbarBanner.navbarBannerImage': this.branding.extra?.navbarBanner?.navbarBannerImage?.s3Uri
            && this.branding.extra.navbarBanner.navbarBannerImage
        };
      });
  }

  private cloneBranding(branding: GungGroupBranding): GungGroupBranding {
    return JSON.parse(JSON.stringify(branding));
  }

  hasBranding(group: string): boolean {
    return this.configuredGroupIds.has(group);
  }

  save(): void {
    if (!this.branding || this.saving) {
      return;
    }
    this.branding.id = this.selectedGroup;
    this.saving = true;
    this.groupBrandingService
      .save(this.toPersistable(this.branding))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: saved => {
          // save() refreshes the subject, so the badge set updates via the getAll() subscription.
          this.branding = this.ensureBrandingDefaults(this.cloneBranding(saved));
          this.saving = false;
          this.gungNotificationService.notify(
            this.translateService.instant('CONFIGURATION_SAVED'),
            this.selectedGroup,
            'success'
          );
        },
        error: err => {
          this.saving = false;
          const message = err?.error?.message || this.translateService.instant('ERROR_SAVING_CONFIGURATION');
          this.gungNotificationService.notify(this.translateService.instant('ERROR'), message, 'error');
        }
      });
  }

  private ensureBrandingDefaults(branding: GungGroupBranding): GungGroupBranding {
    if (!branding.colors) {
      branding.colors = {};
    }
    // Populate every colour key with an explicit empty string. Without this, an unset colour is
    // `undefined`, and the shared input's writeValue ignores undefined/null — so switching from a
    // group that has colours to one that doesn't would leave the previous group's colours on screen.
    for (const key of this.colorKeys) {
      if (branding.colors[key] == null) {
        branding.colors[key] = '';
      }
    }
    if (!branding.extra) {
      branding.extra = {};
    }
    if (!branding.extra.navbarBanner) {
      branding.extra.navbarBanner = {
        backgroundColor: '#000000',
        textColor: '#ffffff',
        text: '',
        bannerHeight: '40',
        textFormatting: '',
        centerText: false,
        enabled: false
      };
    }
    return branding;
  }

  /**
   * Drop empty colour values before persisting, so an unset colour falls back to My Company
   * (per-field fallback) rather than persisting '' and overriding the company colour in the merge.
   */
  private toPersistable(branding: GungGroupBranding): GungGroupBranding {
    const colors: { [s: string]: string } = {};
    for (const [key, val] of Object.entries(branding.colors || {})) {
      if (val !== '' && val != null) {
        colors[key] = val;
      }
    }
    return { ...branding, colors };
  }

  onLogoSelect(files: File[]): void {
    if (!files || files.length === 0) {
      return;
    }
    this.uploadImage(files[0], 'logo').subscribe(image => {
      this.branding.logo = image;
      this.objectFileImagePreview['logo'] = image;
    });
  }

  onBannerImageSelect(files: File[]): void {
    if (!files || files.length === 0) {
      return;
    }
    this.uploadImage(files[0], 'navbarBanner').subscribe(image => {
      this.branding.extra.navbarBanner.navbarBannerImage = image;
      this.objectFileImagePreview['extra.navbarBanner.navbarBannerImage'] = image;
    });
  }

  removeLogo(): void {
    this.branding.logo = emptyS3Image();
    this.objectFileImagePreview['logo'] = null;
  }

  removeBannerImage(): void {
    if (this.branding?.extra?.navbarBanner) {
      this.branding.extra.navbarBanner.navbarBannerImage = emptyS3Image();
    }
    this.objectFileImagePreview['extra.navbarBanner.navbarBannerImage'] = null;
  }

  private uploadImage(file: File, tag: string): Observable<S3Image> {
    const imageObject = {
      index: 0,
      description: `${tag} - ${new Date().getTime()}`,
      category: tag,
      s3Uri: 'images/no-image.jpg'
    };
    const formData = new FormData();
    formData.append('file', file);
    formData.append('productImage', JSON.stringify(imageObject));
    return this.http.post<S3Image>(`import/s3-image`, formData).pipe(map(image => image));
  }
}
